import { DateTime } from 'luxon';
import { checkFail } from '../core.ts';
import { isString } from '../identity.ts';
import { Milliseconds } from '../units.ts';
export { DateTime } from 'luxon';

export function isValidDate(v: DateTime): v is DateTime<true> {
  return v.isValid;
}

export function asValidDate(v: unknown): DateTime<true> | undefined {
  let result: DateTime<boolean>;
  if (v instanceof DateTime) {
    result = v;
  } else if (isString(v)) {
    result = DateTime.fromISO(v);
  } else {
    return undefined;
  }
  return result.isValid ? result : undefined;
}

export function checkValidTime(date: DateTime): asserts date is DateTime<true> {
  if (!date.isValid) {
    checkFail('checkValidTime failed.', () => date.invalidExplanation ?? 'Unknown reason');
  }
}

// Returns "now", but at least one millisecond after |exclusiveMinimum|.
// Useful for generating monotonically increasing timestamps.
export function monotonicNow(exclusiveMinimum: DateTime) {
  return DateTime.fromMillis(Math.max(exclusiveMinimum.toMillis() + 1, DateTime.now().toMillis()));
}

// Is midnight (in `date`s timezone).
export function isMidnight(date: DateTime) {
  return date.hour === 0 && date.minute === 0 && date.second === 0 && date.millisecond === 0;
}

export const DefaultTimeZone = 'America/New_York';

// Returns the earliest of two dates, or the first if the second is undefined.
export function earliest(a: DateTime, b: DateTime | undefined) {
  if (!b) return a;
  return a < b ? a : b;
}

// Returns the latest of two dates, or the first if the second is undefined.
export function latest(a: DateTime, b: DateTime | undefined) {
  if (!b) return a;
  return a > b ? a : b;
}

// Sorts in chronological order, earliest first.
export function timeCompare(a: DateTime, b: DateTime) {
  return a.toMillis() - b.toMillis();
}

export function timeDiff(to: DateTime, from: DateTime) {
  return Milliseconds(to.toMillis() - from.toMillis());
}

export function timeUntil(later: DateTime, earlier?: DateTime) {
  return Milliseconds(later.toMillis() - (earlier?.toMillis() ?? Date.now()));
}

export function timeSince(target: DateTime) {
  return timeUntil(target).times(-1);
}

export function elapsedBetween(a: DateTime, b: DateTime) {
  return Milliseconds(Math.abs(a.toMillis() - b.toMillis()));
}

export function elapsedSince(dt: DateTime) {
  const result = Milliseconds(DateTime.now().toMillis() - dt.toMillis());
  // console.log('elapsedSince', { result, dt });
  return result;
}

// DNS: Test
export function isFirstWeekOfMonth(date: DateTime, options: { midnight: 'include' | 'exclude' }) {
  if (options.midnight === 'exclude' && isMidnight(date)) {
    return date.day >= 2 && date.day <= 8;
  } else {
    return date.day <= 7;
  }
}

// DNS: Test
export function isYearEnd(date: DateTime) {
  return isMidnight(date) && date.month === 1 && date.day === 1;
}
